import dayjs, { type Dayjs } from 'dayjs';
import isNil from 'lodash/fp/isNil';
import {
  type FunctionComponent,
  type InputHTMLAttributes,
  type PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from 'react';
import DatePicker from 'react-datepicker';

import { convertDateInUtcToLocalWithoutOffset, convertLocalDateToUTCWithoutOffset } from 'components/date.utils';
import CalendarPredefinedRangesList from './CalendarPredefinedRangesList';
import CalendarWrapper from './CalendarWrapper';
import type { PredefinedRange } from '../../../predefinedDateRanges';
import { createContext } from 'react';
import { ensureDateRangeWithinBounds } from '../../../dateRange.utils';
import { Typography } from '@mui/joy';

type DateRangeContextType = {
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  onConfirm: (val: [Dayjs, Dayjs | null] | null) => void;
  onCancel: () => void;
  minDate: Dayjs | undefined | null;
  maxDate: Dayjs | undefined | null;
  predefinedRanges?: PredefinedRange[];
  showDaysSelected?: boolean;
  setDateRange: (val: [Dayjs | null, Dayjs | null]) => void;
  allowSelectingOnlySince?: boolean;
};

const defaultDateRangeContext: DateRangeContextType = {
  startDate: null,
  endDate: null,
  onConfirm: () => {
    // pass
  },
  onCancel: () => {
    // pass
  },
  minDate: undefined,
  maxDate: undefined,
  setDateRange: () => {
    // pass
  },
  allowSelectingOnlySince: false,
};

const DateRangeContext = createContext<DateRangeContextType>(defaultDateRangeContext);

const DatePickerContainer: FunctionComponent<PropsWithChildren> = ({ children }) => {
  const {
    startDate,
    endDate,
    onConfirm,
    onCancel,
    minDate,
    maxDate,
    predefinedRanges,
    showDaysSelected,
    setDateRange,
    allowSelectingOnlySince,
  } = useContext(DateRangeContext);

  const selectedDaysCount = startDate && endDate ? endDate.diff(startDate, 'day') : undefined;
  return (
    <CalendarWrapper
      footerMessage={
        showDaysSelected && !isNil(selectedDaysCount) ? (
          <Typography>Days selected: {selectedDaysCount}</Typography>
        ) : undefined
      }
      confirmationDisabled={!isNil(startDate) && !allowSelectingOnlySince && isNil(endDate)}
      onConfirm={() => {
        if (isNil(startDate)) {
          onConfirm(null);
        } else {
          onConfirm(ensureDateRangeWithinBounds(minDate, maxDate, [startDate, endDate], allowSelectingOnlySince));
        }
      }}
      onCancel={onCancel}
      predefinedRangesSelector={
        predefinedRanges ? (
          <CalendarPredefinedRangesList
            predefinedRanges={predefinedRanges}
            maxDate={maxDate ?? undefined}
            minDate={minDate ?? undefined}
            setDateRange={setDateRange}
          />
        ) : undefined
      }
    >
      {children}
    </CalendarWrapper>
  );
};

type DateRangeInputCalendarProps<AllowOnlySince extends boolean = false> = {
  defaultValue: [Dayjs, AllowOnlySince extends true ? Dayjs | null : Dayjs] | null;
  onConfirm: (val: [Dayjs, AllowOnlySince extends true ? Dayjs | null : Dayjs] | null) => void;
  onCancel: () => void;
  minDate?: Dayjs | null;
  maxDate?: Dayjs | null;
  predefinedRanges?: PredefinedRange[];
  showDaysSelected?: boolean;
  disabled?: boolean;
  allowSelectingOnlySince?: AllowOnlySince;
} & Pick<InputHTMLAttributes<HTMLInputElement>, 'onFocus' | 'onBlur'>;

const calculateMinMaxDate = (value: null | [Dayjs, Dayjs | null]): [Dayjs | null, Dayjs | null] => {
  if (isNil(value)) {
    return [null, null];
  }
  return value;
};

/* 
we try to keep all dates in utc, but DatePicker input/output dates are local, we need to convert it to utc 
and we need to convert component inputs maxDate, minDate, startDate, endDate from utc to local for DatePicker
*/
const DateRangeInputCalendar = <AllowOnlySince extends boolean = false>({
  defaultValue,
  onConfirm,
  onCancel,
  minDate,
  maxDate,
  predefinedRanges,
  showDaysSelected,
  disabled,
  allowSelectingOnlySince,
}: DateRangeInputCalendarProps<AllowOnlySince>) => {
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>(calculateMinMaxDate(defaultValue));

  useEffect(() => {
    setDateRange(calculateMinMaxDate(defaultValue));
  }, [defaultValue]);

  const [startDate, endDate] = dateRange ?? [];
  const finalStartDate =
    !isNil(startDate) && startDate.isValid() ? convertDateInUtcToLocalWithoutOffset(startDate).toDate() : undefined;
  return (
    // purpose of context is communication with DatePickerContainer
    <DateRangeContext.Provider
      value={{
        startDate,
        endDate,
        onConfirm: onConfirm as (val: [Dayjs, Dayjs | null] | null) => void, // it's not possible to have generic context in react, so we need to cast to more general type
        onCancel,
        minDate,
        maxDate,
        predefinedRanges,
        showDaysSelected,
        setDateRange,
        allowSelectingOnlySince,
      }}
    >
      <DatePicker
        disabledKeyboardNavigation
        minDate={convertDateInUtcToLocalWithoutOffset(minDate?.utc())?.toDate()}
        maxDate={convertDateInUtcToLocalWithoutOffset(maxDate?.utc())?.toDate()}
        startDate={finalStartDate}
        endDate={
          !isNil(endDate) && endDate.isValid() ? convertDateInUtcToLocalWithoutOffset(endDate).toDate() : undefined
        }
        key={finalStartDate?.toISOString()} // use key to reset calendar in case someone clicks predefined ranges - this way calendar will switch to start date
        disabled={disabled}
        selectsRange
        inline
        onChange={([newStartDateLocal, newEndDateLocal]): void => {
          // to cover most of the cases take start of the start day and end of the end day
          // and it doesn't change anything in case when we send only dates without time
          const start = newStartDateLocal
            ? convertLocalDateToUTCWithoutOffset(dayjs(newStartDateLocal)).startOf('day')
            : null;
          const end = newEndDateLocal ? convertLocalDateToUTCWithoutOffset(dayjs(newEndDateLocal)).endOf('day') : null;
          setDateRange([start, end]);
        }}
        calendarContainer={DatePickerContainer}
      />
    </DateRangeContext.Provider>
  );
};

export default DateRangeInputCalendar;
